import ErrorResponse from "./ErrorResponse";
import HttpMethod from "./HttpMethod";
import { HTTP_SUCCESS_STATUS } from "./ResponseStatus";
import { isNativeMode } from "../../native/nativeMode";
import { reportRequestTiming } from "./requestTiming";
import { isServedNativeConflict, leaveLegacySession } from "../../native/legacySessionServedNative";

// Base del monolito (api.life5.it). En el funnel native (native.life5.it) el
// monolito es OTRO origen → CORS, y el lead-token del gateway no vale allí → 401.
// Cualquier llamada residual al monolito rompe el funnel (el controlador redirige
// a /login). El choke-point la neutraliza (ver fetchRequest).
const MONOLITH_BASE = `${process.env.REACT_APP_CUSTOMER_APP_URL ?? ""}`.replace(
  /\/+$/,
  ""
);

interface Fetch {
  path: string | RequestInfo;
  body?: any;
  method?: HttpMethod;
  mode?: RequestMode;
  extraHeaders?: any;
}

interface PetitionI extends Omit<Fetch, "extraHeaders" | "method"> {
  headers?: any;
}

const parseError = async (response: Response): Promise<ErrorResponse> => {
  let error: ErrorResponse;
  try {
    const errorJson = await response.json();
    error = {
      status: response.status,
      error: errorJson.errorCode,
      message:
        errorJson.errorMessage ||
        errorJson.message ||
        errorJson.brokerLanding ||
        (typeof errorJson.error === "string" ? errorJson.error : undefined),
    };
    if ("string" === typeof errorJson.enPalabras) {
      error.enPalabras = errorJson.enPalabras;
    }
    if ("string" === typeof errorJson.nativeLoginToken) {
      error.nativeLoginToken = errorJson.nativeLoginToken;
    }
    if ("string" === typeof errorJson.nativePortalUrl) {
      error.nativePortalUrl = errorJson.nativePortalUrl;
    }
    // 422 INVALID_RESIDENTIAL_ZIP_CITY del guardado del asegurado (native): el código
    // (el message es el texto técnico de ms-agl) y los CAP del comune. Solo con
    // validZipRanges: el resto de errores queda como siempre.
    if (Array.isArray(errorJson.validZipRanges)) {
      error.validZipRanges = errorJson.validZipRanges;
      if ("string" === typeof errorJson.error) {
        error.code = errorJson.error;
      }
    }
    // «Modifica capitale» (native): el código del cuerpo y sus bloqueos o su
    // motivo. Solo cuando vienen: el resto de errores queda como siempre.
    if ("string" === typeof errorJson.error) {
      if (Array.isArray(errorJson.blockers)) {
        error.blockers = errorJson.blockers.filter(
          (b: unknown): b is string => "string" === typeof b
        );
        error.code = errorJson.error;
      }
      if ("string" === typeof errorJson.reason) {
        error.reason = errorJson.reason;
        error.code = errorJson.error;
      }
    }
  } catch (e) {
    error = {
      status: response.status,
      error: response.statusText,
      message: "",
    };
  }

  return error;
};

const createResponse = async (response: Response, path?: Fetch["path"]): Promise<any> => {
  const responseStatus = response.status;
  let result: any;
  if (!HTTP_SUCCESS_STATUS.includes(responseStatus)) {
    const responseData: ErrorResponse = await parseError(response);
    // El portal legacy ya no sirve a esta póliza (cartera IT con la lectura native):
    // se sale a native en vez de enseñar el error. Navegando, la llamada no se
    // resuelve (la página se va); si no se puede, el error de siempre.
    if (
      isServedNativeConflict(responseStatus, responseData.message, path) &&
      (await leaveLegacySession(responseData))
    ) {
      return new Promise(() => undefined);
    }
    throw responseData;
  } else {
    try {
      result = await response.json();
    } catch (e) {
      result = response;
    }
  }
  return result;
};

const fetchRequest = ({
  path,
  body,
  method,
  mode,
  extraHeaders,
}: Fetch): Promise<any> =>
  new Promise((resolve, reject) => {
    // Choke-point native: no se llama al monolito desde el funnel native. Las
    // llamadas native-aware van al gateway (otra base) y pasan; las que quedan
    // apuntando al monolito se neutralizan (resuelve vacío, SIN fetch) para no
    // disparar CORS/401 → redirect a login. Lo que necesita datos (quote,
    // boundaries, validate-age, /customer/leads/*) ya está ruteado al gateway.
    if (
      isNativeMode() &&
      "" !== MONOLITH_BASE &&
      "string" === typeof path &&
      path.startsWith(MONOLITH_BASE)
    ) {
      // eslint-disable-next-line no-console
      console.warn("[native] llamada al monolito neutralizada:", path);
      resolve({});
      return;
    }
    const sendInformation = body
      ? {
          method: method,
          body: JSON.stringify(body),
        }
      : {};
    // Duración de la llamada (hasta tener la respuesta leída o el error) para
    // la telemetría (api_slow).
    const startedAt = Date.now();
    const timed = () =>
      reportRequestTiming(
        "string" === typeof path ? path : (path as Request).url,
        Date.now() - startedAt
      );
    fetch(path, {
      mode: mode ?? "cors",
      headers: {
        ...extraHeaders,
      },
      ...sendInformation,
    })
      .then((response) => {
        return createResponse(response, path);
      })
      .then(
        (result) => {
          timed();
          resolve(result);
        },
        (error) => {
          timed();
          reject(error);
        }
      );
  });

export const get = ({ path, headers, mode }: PetitionI) =>
  fetchRequest({ path, extraHeaders: headers, mode });

export const post = ({ path, headers, mode, body }: PetitionI) =>
  fetchRequest({
    path,
    body,
    method: HttpMethod.POST,
    extraHeaders: headers,
    mode,
  });

export const put = ({ path, headers, mode, body }: PetitionI) =>
  fetchRequest({
    path,
    body,
    method: HttpMethod.PUT,
    extraHeaders: headers,
    mode,
  });

export const patch = ({ path, headers, mode, body }: PetitionI) =>
  fetchRequest({
    path,
    body,
    method: HttpMethod.PATCH,
    extraHeaders: headers,
    mode,
  });

export const delet = ({ path, headers, mode, body }: PetitionI) =>
  fetchRequest({
    path,
    body,
    method: HttpMethod.DELETE,
    extraHeaders: headers,
    mode,
  });
